import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard, Users, FileText, Receipt, HardHat, Wrench, FolderArchive, Bot,
  ChevronLeft, ChevronRight, Building2, ClipboardList, ShoppingCart, Package,
  ShieldCheck, History, LogOut, Settings, CheckSquare, MessageSquare, Target, Sparkles,
  Calendar as CalendarIcon, Wallet, Zap, FolderOpen, MessageCircle, MapPin,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

/**
 * Navigation groupée par catégorie (identique à la structure mobile drawer)
 * pour un repère cognitif clair et un accès rapide.
 */
const navSections = [
  {
    title: 'Pilotage',
    items: [
      { to: '/', icon: LayoutDashboard, label: 'Tableau de bord', exact: true, module: 'dashboard' },
      { to: '/agenda', icon: CalendarIcon, label: 'Agenda', module: 'agenda' },
      { to: '/taches', icon: CheckSquare, label: 'Tâches', module: 'taches' },
      { to: '/discussions', icon: MessageSquare, label: 'Discussions', module: 'chat' },
    ],
  },
  {
    title: 'Clients & prospection',
    items: [
      { to: '/contacts', icon: Users, label: 'Contacts', module: 'contacts' },
      { to: '/gestion-clients?category=collectivites', icon: Building2, label: 'Collectivités', module: 'gestion_clients' },
      { to: '/prospections', icon: Target, label: 'Prospections', module: 'prospections' },
    ],
  },
  {
    title: 'Devis, factures, achats',
    items: [
      { to: '/devis', icon: FileText, label: 'Devis', module: 'devis' },
      { to: '/factures', icon: Receipt, label: 'Factures', module: 'factures' },
      { to: '/factures-achat', icon: ShoppingCart, label: "Factures d'achat", module: 'factures_achat' },
    ],
  },
  {
    title: 'Terrain',
    items: [
      { to: '/fiches-visite', icon: ClipboardList, label: 'Fiches Visite', module: 'fiches_visite' },
      { to: '/chantiers', icon: HardHat, label: 'Chantiers', module: 'chantiers' },
      { to: '/sous-traitants', icon: Wrench, label: 'Sous-traitants', module: 'sous_traitants' },
      { to: '/fournisseurs', icon: Package, label: 'Fournisseurs', module: 'sous_traitants' },
    ],
  },
  {
    title: 'IA & marketing',
    items: [
      { to: '/marketing', icon: Sparkles, label: 'Community Manager', module: 'marketing' },
      { to: '/agents', icon: Bot, label: 'Orchestrateur IA', module: 'agents_ia' },
    ],
  },
  {
    title: 'CEE & AIDE',
    items: [
      { to: '/cee/simulations', icon: Zap, label: 'Simulations', module: 'cee' },
      { to: '/cee/dossiers', icon: FolderOpen, label: 'Dossiers', module: 'cee' },
      { to: '/cee/zones', icon: MapPin, label: 'Vue par zone', module: 'cee' },
    ],
  },
  {
    title: 'Administratif',
    items: [
      { to: '/administratif', icon: FolderArchive, label: 'Administratif', module: 'administratif' },
    ],
  },
];

const adminItems = [
  { to: '/comptabilite', icon: Wallet, label: 'Comptabilité' },
  { to: '/admin/users', icon: ShieldCheck, label: 'Utilisateurs' },
  { to: '/admin/amos', icon: Users, label: 'AMO' },
  { to: '/whatsapp/admin', icon: MessageCircle, label: 'WhatsApp' },
  { to: '/admin/activity-log', icon: History, label: 'Activité' },
  { to: '/parametres', icon: Settings, label: 'Paramètres' },
];

function canSee(user, module) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  const perms = user.permissions || {};
  const entry = perms[module];
  if (entry === undefined) return true;
  return !!entry?.read;
}

export default function Sidebar({ collapsed, setCollapsed }) {
  const { user, logout } = useAuth();
  const isAdmin = user && user.role === 'admin';

  // Filtre chaque section par permissions ; masque les sections vides.
  const visibleSections = navSections
    .map((s) => ({ ...s, items: s.items.filter((it) => canSee(user, it.module)) }))
    .filter((s) => s.items.length > 0);

  return (
    <aside
      className={`fixed left-0 top-0 h-full bg-white border-r border-slate-200 z-40 transition-all duration-300 ${
        collapsed ? 'w-16' : 'w-64'
      }`}
    >
      <div className="flex flex-col h-full">
        <div className="p-4 border-b border-slate-200">
          {!collapsed ? (
            <div className="flex items-center justify-between">
              <img src="/logo.png" alt="Reno Resolve" className="h-16 w-auto" />
              <button
                onClick={() => setCollapsed(!collapsed)}
                className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
                data-testid="sidebar-toggle-btn"
              >
                <ChevronLeft className="w-5 h-5 text-slate-600" />
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-blue-700 flex items-center justify-center">
                <span className="text-white font-bold text-base">RR</span>
              </div>
              <button
                onClick={() => setCollapsed(!collapsed)}
                className="p-1 rounded-lg hover:bg-slate-100 transition-colors"
                data-testid="sidebar-toggle-btn"
              >
                <ChevronRight className="w-4 h-4 text-slate-600" />
              </button>
            </div>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {visibleSections.map((section, idx) => (
            <div key={section.title} className={idx > 0 ? 'pt-3' : ''}>
              {!collapsed && (
                <p className="px-3 mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  {section.title}
                </p>
              )}
              {section.items.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.exact}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 ${
                        isActive
                          ? 'bg-blue-50 text-blue-700 font-medium'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      } ${collapsed ? 'justify-center' : ''}`
                    }
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                    title={collapsed ? item.label : undefined}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    {!collapsed && <span className="text-sm">{item.label}</span>}
                  </NavLink>
                );
              })}
            </div>
          ))}

          {isAdmin && (
            <>
              <div className={`pt-3 mt-3 border-t border-slate-100 ${collapsed ? '' : 'px-2'}`}>
                {!collapsed && (
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Administration</p>
                )}
              </div>
              {adminItems.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                        isActive
                          ? 'bg-red-50 text-red-700 font-medium'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      } ${collapsed ? 'justify-center' : ''}`
                    }
                    data-testid={`nav-admin-${item.label.toLowerCase()}`}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    {!collapsed && <span>{item.label}</span>}
                  </NavLink>
                );
              })}
            </>
          )}
        </nav>

        {user && user !== false && (
          <div className="p-3 border-t border-slate-200">
            {!collapsed ? (
              <div className="space-y-2" data-testid="sidebar-user-footer">
                <div className="flex items-center gap-2 px-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center text-sm font-semibold flex-shrink-0">
                    {(user.name || user.email || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-900 truncate" data-testid="sidebar-user-name">{user.name || '—'}</p>
                    <p className="text-[10px] text-slate-400 truncate">{user.email}</p>
                  </div>
                </div>
                <button
                  onClick={logout}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs text-slate-600 hover:bg-red-50 hover:text-red-600 transition-colors"
                  data-testid="sidebar-logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                  Se déconnecter
                </button>
              </div>
            ) : (
              <button
                onClick={logout}
                className="w-full flex items-center justify-center p-2 rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-600"
                title="Se déconnecter"
                data-testid="sidebar-logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </button>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}
